import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColorPicker } from "primereact/colorpicker";
import type { ColorPickerChangeEvent } from "primereact/colorpicker";
import { useToast } from "../../context/ToastContext";
import GroupApi, { UpdateGroupPayload } from "../../services/GroupApi";

// Design System
import FormCard from "../../components/FormCard/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const HEX_COLOR_REGEX = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

type RouteParams = {
  id?: string;
  groupId?: string;
};

type GroupDetails = {
  id: string;
  name: string;
  description: string;
  color: string;
};

const sanitizeHexColor = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  const match = trimmed.match(HEX_COLOR_REGEX);
  if (!match) {
    return null;
  }

  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
};

const extractGroupPayload = (payload: any): any => {
  if (payload && typeof payload === "object") {
    if (payload.group) {
      return payload.group;
    }

    if (payload.Group) {
      return payload.Group;
    }
  }

  return payload;
};

const normalizeGroupDetails = (payload: any): GroupDetails | null => {
  const source = extractGroupPayload(payload);

  if (!source || typeof source !== "object") {
    return null;
  }

  const id = source.id ?? source.groupId ?? source.groupID ?? source.Id;
  if (!id) {
    return null;
  }

  const name = source.name ?? source.groupName ?? source.description ?? source.GroupName ?? "";
  const description = source.description ?? source.groupDescription ?? source.Description ?? "";
  const color = source.color ?? source.Color ?? source.groupColor ?? source.Colour ?? "";

  return {
    id: String(id),
    name: typeof name === "string" ? name : String(id),
    description: typeof description === "string" ? description : "",
    color: typeof color === "string" ? color : "",
  };
};

const EditGroupForm: React.FC = () => {
  const params = useParams<RouteParams>();
  const groupId = params.groupId ?? params.id ?? "";

  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("Messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const nameRef = useRef<HTMLInputElement | null>(null);
  const colorRef = useRef<HTMLInputElement | null>(null);
  const colorPickerRef = useRef<ColorPicker | null>(null);

  const showErrorToast = useCallback(
    (errorData: any, fallbackMessage: string, duration = 3000): void => {
      const summary = translateMessages("error") || "Erro";
      const detail =
        errorData?.response?.data?.message ??
        errorData?.response?.data?.error?.message ??
        errorData?.data?.message ??
        errorData?.message ??
        fallbackMessage ??
        summary;
      displayToast("error", summary, detail, false, duration);
    },
    [displayToast, translateMessages],
  );

  useEffect(() => {
    if (!groupId) {
      displayToast(
        "error",
        translateMessages("error") || "Erro",
        translateForm("groupNotFound") || "Grupo não encontrado",
        false,
        3000,
      );
      navigate("/groups");
      return;
    }

    const loadGroup = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await GroupApi.GetById(groupId);
        const payload = response?.data ?? response;
        const normalized = normalizeGroupDetails(payload);

        if (!normalized) {
          throw new Error("GROUP_NOT_FOUND");
        }

        setName(normalized.name);
        setDescription(normalized.description ?? "");
        setColor(normalized.color ?? "");
      } catch (err) {
        showErrorToast(err, translateForm("groupLoadFailed") || "Não foi possível carregar o grupo", 4000);
        navigate("/groups");
      } finally {
        setLoading(false);
      }
    };

    void loadGroup();
  }, [displayToast, groupId, navigate, showErrorToast, translateForm, translateMessages]);

  const sanitizedColorValue = useMemo(() => sanitizeHexColor(color), [color]);

  const colorPreviewStyle = useMemo(() => {
    if (!sanitizedColorValue || sanitizedColorValue === "") {
      return { backgroundColor: "transparent", border: "1px solid rgba(15, 23, 42, 0.2)" };
    }

    return { backgroundColor: sanitizedColorValue, border: "1px solid rgba(15, 23, 42, 0.2)" };
  }, [sanitizedColorValue]);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (!groupId) {
      displayToast(
        "error",
        translateMessages("error") || "Erro",
        translateForm("groupNotFound") || "Grupo não encontrado",
        false,
        3000,
      );
      return;
    }

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const sanitizedColor = sanitizeHexColor(color);

    if (!trimmedName || sanitizedColor === null) {
      setError(true);

      if (!trimmedName) {
        displayToast(
          "error",
          translateMessages("error") || "Erro",
          translateForm("requiredField") || "Campo obrigatório",
          false,
          3000,
        );
        nameRef.current?.focus?.();
        return;
      }

      if (sanitizedColor === null) {
        displayToast(
          "error",
          translateMessages("error") || "Erro",
          translateForm("invalidColor") || "Cor inválida",
          false,
          3000,
        );
        colorRef.current?.focus?.();
        return;
      }
    }

    const payload: UpdateGroupPayload = {
      id: groupId,
      name: trimmedName,
      description: trimmedDescription,
      color: sanitizedColor || "",
    };

    try {
      setSaving(true);
      await GroupApi.Update(payload);
      displayToast(
        "success",
        translateForm("Success") || "Sucesso",
        translateForm("groupUpdated") || "Grupo atualizado com sucesso",
        false,
        3000,
      );
      navigate("/groups");
    } catch (err) {
      showErrorToast(err, translateForm("groupNotUpdated") || "Não foi possível atualizar o grupo", 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleColorPickerChange = useCallback((event: ColorPickerChangeEvent): void => {
    const rawValue = typeof event.value === "string" ? event.value : "";
    const normalized = rawValue.replace("#", "").trim();
    setColor(normalized ? `#${normalized}` : "");
    setError(false);
  }, []);

  const handleColorFieldFocus = useCallback((): void => {
    if (loading || saving) {
      return;
    }
    colorPickerRef.current?.show();
  }, [loading, saving]);

  return (
    <FormCard
      title={translateForm("editGroup")}
      icon="edit"
      onSubmit={handleSubmit}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translateGeneral("save") || "Salvar"}
      cancelButtonLabel={translateGeneral("cancel") || "Cancelar"}
      loading={saving}
      disabled={loading}
      numColumns={12}
    >
      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="text"
          ref={nameRef}
          label={translateForm("name")}
          required
          value={name}
          maxLength={120}
          onChange={(val: any) => {
            setName(val);
            setError(false);
          }}
          placeholder={translateForm("name")}
          className={error && !name.trim() ? "p-invalid" : ""}
        />
      </div>

      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="textArea"
          label={translateForm("description")}
          value={description}
          rows={4}
          maxLength={500}
          onChange={(val: any) => {
            setDescription(val);
            setError(false);
          }}
          placeholder={translateForm("description")}
        />
      </div>

      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="text"
          ref={colorRef}
          label={translateForm("color")}
          value={color}
          maxLength={7}
          onChange={(val: any) => {
            setColor(val);
            setError(false);
          }}
          onFocus={handleColorFieldFocus}
          onClick={handleColorFieldFocus}
          placeholder="#1F2937"
          icon="palette"
          className={error && sanitizedColorValue === null ? "p-invalid" : ""}
          labelAction={
            <div
              aria-hidden="true"
              onClick={handleColorFieldFocus}
              className="border-circle"
              style={{
                width: "1.25rem",
                height: "1.25rem",
                borderRadius: "50%",
                cursor: loading || saving ? "not-allowed" : "pointer",
                ...colorPreviewStyle,
              }}
            />
          }
        />
        <ColorPicker
          ref={colorPickerRef}
          value={sanitizedColorValue && sanitizedColorValue !== "" ? sanitizedColorValue.replace("#", "") : ""}
          onChange={handleColorPickerChange}
          format="hex"
          disabled={loading || saving}
          style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
        />
        {error && sanitizedColorValue === null && (
          <small className="p-error">{translateForm("invalidColor") || "Cor inválida"}</small>
        )}
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/groups")}
      />
    </FormCard>
  );
};

export default EditGroupForm;
